/**
 * Life4Earth V2 — Embed Allowlist API
 *
 * Frontend client for the embed domain whitelist. The GET endpoint is
 * available to all authenticated users (needed for client-side URL
 * validation in the TipTap editor). Write endpoints are system-admin-only.
 */

import { apiFetch } from './client';

// ── Types ─────────────────────────────────────────────────────────

export interface EmbedAllowedDomain {
  id: string;
  domain: string;
  label: string;
  category: string;
  defaultHeight: number;
  sandbox: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface AddEmbedDomainRequest {
  domain: string;
  label: string;
  category?: string;
  defaultHeight?: number;
  sandbox?: string;
  notes?: string;
}

export interface UpdateEmbedDomainRequest {
  label?: string;
  category?: string;
  defaultHeight?: number;
  sandbox?: string;
  isActive?: boolean;
  notes?: string;
}

// ── Read ──────────────────────────────────────────────────────────

/** Fetch all active embed-allowed domains. */
export async function fetchEmbedAllowlist(): Promise<EmbedAllowedDomain[]> {
  return apiFetch<EmbedAllowedDomain[]>('/api/embed-allowlist');
}

/** Fetch all embed-allowed domains including inactive ones (admin). */
export async function fetchEmbedAllowlistFull(): Promise<EmbedAllowedDomain[]> {
  return apiFetch<EmbedAllowedDomain[]>('/api/embed-allowlist?includeInactive=true');
}

// ── Write (system admin) ──────────────────────────────────────────

/** Add a domain to the embed allowlist. */
export async function addEmbedDomain(
  req: AddEmbedDomainRequest
): Promise<EmbedAllowedDomain> {
  return apiFetch<EmbedAllowedDomain>('/api/embed-allowlist', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** Update an existing embed-allowed domain. */
export async function updateEmbedDomain(
  id: string,
  req: UpdateEmbedDomainRequest
): Promise<EmbedAllowedDomain> {
  return apiFetch<EmbedAllowedDomain>(`/api/embed-allowlist/${id}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
}

/** Deactivate (soft-delete) an embed-allowed domain. */
export async function deactivateEmbedDomain(id: string): Promise<void> {
  await apiFetch<void>(`/api/embed-allowlist/${id}`, {
    method: 'DELETE',
  });
}
